<template>
  <v-menu :offset="14">
    <template #activator="{ props, isActive }">
      <button
        v-bind="props"
        type="button"
        class="dp-role-button"
        aria-label="Switch Datapeople role"
        :class="{ active: isActive }"
      >
        <span class="dp-role-label text-body-1">
          JobNav Recruiter {{ currentRoleLabel ?? 'Role' }}
        </span>
        <v-icon
          icon="$chevron-down"
          :size="20"
        />
      </button>
    </template>
    <section class="dropdown-container">
      <ul class="role-list">
        <li
          v-for="role in availableRoles"
          :key="role.value"
          class="role-item"
          :class="{ 'role-item--active': role.value === currentRoleKey }"
        >
          <button
            type="button"
            class="role-item-btn text-body-2"
            :disabled="role.value === currentRoleKey"
            @click="onSelect(role.value)"
          >
            {{ role.title }}
          </button>
        </li>
      </ul>
    </section>
  </v-menu>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { switchDatapeopleRole } from '@/api/settings';
import {
  RECRUITER_ROLE_LABELS, ROLE_KEY_EDITOR, ROLE_KEY_ADMIN, ROLE_KEY_IT_ADMIN,
} from '@/helpers/constants/recruiterRoles';

defineOptions({ name: 'DatapeopleRoleDropdown' });

const ROLE_NAME_TO_KEY = { editor: ROLE_KEY_EDITOR, admin: ROLE_KEY_ADMIN, itAdmin: ROLE_KEY_IT_ADMIN };
const DP_ROLE_NAMES = Object.keys(ROLE_NAME_TO_KEY);

const BASE_ROLES = [
  { title: RECRUITER_ROLE_LABELS[ROLE_KEY_EDITOR], value: ROLE_KEY_EDITOR },
  { title: RECRUITER_ROLE_LABELS[ROLE_KEY_ADMIN], value: ROLE_KEY_ADMIN },
];
const IT_ADMIN_ROLE = { title: RECRUITER_ROLE_LABELS[ROLE_KEY_IT_ADMIN], value: ROLE_KEY_IT_ADMIN };

const { authData } = storeToRefs(useAppAuthData());

const currentRoleKey = computed(() => {
  const match = authData.value.rbac?.roleNames?.find((r) => DP_ROLE_NAMES.includes(r));
  if (!match) return null;
  return ROLE_NAME_TO_KEY[match];
});

const currentRoleLabel = computed(
  () => RECRUITER_ROLE_LABELS[currentRoleKey.value] ?? null,
);

const availableRoles = computed(() => (
  authData.value.isRoleManagementRestricted
    ? [...BASE_ROLES, IT_ADMIN_ROLE]
    : BASE_ROLES
));

const onSelect = async (roleKey) => {
  await switchDatapeopleRole(roleKey);
  window.location.reload();
};
</script>

<style lang="less" scoped>
.dp-role-button {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;

  .dp-role-label {
    color: @text-white;
    padding: 0px 8px;
  }

  :deep(svg path) {
    fill: @icon-white;
  }

  :deep(.v-icon) {
    transition: 300ms all ease-in-out;
  }

  &.active {
    :deep(.v-icon) {
      transform: rotateZ(180deg);
    }
  }
}

.dropdown-container {
  background-color: @bg-default;
  border-radius: 4px;
  border: 1px solid @stroke-dark;
  padding: 12px 16px;
}

li {
  list-style-type: none;
}

.role-list {
  min-width: 160px;
}

.role-item {
  margin-top: 4px;
  border-radius: 8px;
  display: flex;
  align-items: center;

  &--active {
    background-color: @bg-dark;
    border: 1px solid @stroke-default;
  }

  &:not(.role-item--active):hover {
    background-color: @bg-dark;
  }
}

.role-item-btn {
  display: inline-flex;
  align-items: center;
  width: 100%;
  color: @text-darkest;
  padding: 6px 8px;

  &:disabled {
    cursor: default;
  }
}
</style>
